import {
  Button,
  Card,
  DatePicker,
  ExportButton,
  Icons,
  Image,
  Input,
  notification,
  Table,
  Typography,
} from '@pankod/refine-antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useState } from 'react';
import { getSupplierReport, ISupplierReportItem } from 'services/supplier';
import { downloadCSVFile } from 'utilities/file';

dayjs.extend(utc);

const { RangePicker } = DatePicker;
const { Text } = Typography;

const yearStartingDate = dayjs().startOf('year');

type SupplierReportTableProps = {
  supplierId: string;
};

export const SupplierReportTable: React.FC<SupplierReportTableProps> = ({ supplierId }) => {
  const [data, setData] = useState<ISupplierReportItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    yearStartingDate.startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [start, end] = dateRange;

  // Debounce search query to prevent excessive database calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrent(1); // Reset page to 1 when search query changes
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await getSupplierReport({
        supplierId,
        startDate: start.utc().toISOString(),
        endDate: end.utc().toISOString(),
        start: 1,
        limit: 1000000,
        query: debouncedQuery.trim() || undefined,
      });

      const rows: any[][] = [
        [
          'Product Name',
          'SKU',
          'Procured Qty',
          'Sold Qty',
          'Returned Qty',
          'Net Sold Qty',
          'In Stock Qty',
          'Supplier Price',
          'Total Sales (Supplier Cost)',
          'Net Sales (Supplier Cost)',
        ],
      ];

      (response.data || []).forEach((element) => {
        rows.push([
          element.productName || '-',
          element.sku || '-',
          element.quantityProcured?.toString() || '0',
          element.quantitySold?.toString() || '0',
          element.quantityReturned?.toString() || '0',
          element.netSoldQuantity?.toString() || '0',
          element.quantityInStock?.toString() || '0',
          element.supplierPrice?.toString() || '0',
          element.totalSalesValue?.toString() || '0',
          element.netSalesValue?.toString() || '0',
        ]);
      });

      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExporting(false);
  };

  const fetchReport = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getSupplierReport({
        supplierId,
        startDate: start.utc().toISOString(),
        endDate: end.utc().toISOString(),
        start: current,
        limit: pageSize,
        query: debouncedQuery.trim() || undefined,
      });
      setData(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed to load report',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [supplierId, start, end, current, pageSize, debouncedQuery]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const disabledDate = (date: Dayjs) => date.isAfter(dayjs(), 'day');

  const columns = [
    {
      title: 'Image',
      dataIndex: 'productImage',
      key: 'productImage',
      width: 70,
      fixed: 'left' as const,
      render: (url?: string) =>
        url ? (
          <Image
            width={40}
            height={40}
            src={url}
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback="https://via.placeholder.com/40"
          />
        ) : (
          <div style={{ width: 40, height: 40, background: '#f0f0f0', borderRadius: '4px' }} />
        ),
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      fixed: 'left' as const,
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: 'Procured Qty',
      dataIndex: 'quantityProcured',
      key: 'quantityProcured',
      align: 'right' as const,
    },
    {
      title: 'Sold Qty',
      dataIndex: 'quantitySold',
      key: 'quantitySold',
      align: 'right' as const,
    },
    {
      title: 'Returned Qty',
      dataIndex: 'quantityReturned',
      key: 'quantityReturned',
      align: 'right' as const,
    },
    {
      title: 'Net Sold Qty',
      dataIndex: 'netSoldQuantity',
      key: 'netSoldQuantity',
      align: 'right' as const,
      render: (val: number) => <strong>{val}</strong>,
    },
    {
      title: 'In Stock Qty',
      dataIndex: 'quantityInStock',
      key: 'quantityInStock',
      align: 'right' as const,
    },
    {
      title: 'Supplier Price',
      dataIndex: 'supplierPrice',
      key: 'supplierPrice',
      align: 'right' as const,
      render: (val: number) => `TK ${val.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Total Sales (Supplier Cost)',
      dataIndex: 'totalSalesValue',
      key: 'totalSalesValue',
      align: 'right' as const,
      width: 140,
      render: (val: number) => `TK ${val.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Net Sales (Supplier Cost)',
      dataIndex: 'netSalesValue',
      key: 'netSalesValue',
      align: 'right' as const,
      width: 140,
      render: (val: number) => (
        <strong style={{ color: '#52c41a' }}>
          TK {val.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </strong>
      ),
    },
  ];

  return (
    <Card
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <span>Supplier Reports</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <Input
              placeholder="Search Product or SKU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Text style={{ fontSize: '0.9rem', fontWeight: 500 }}>Select Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={(values) => {
                  if (values && values[0] && values[1]) {
                    setDateRange([values[0].startOf('day'), values[1].endOf('day')]);
                  }
                }}
                format="DD-MM-YYYY"
                disabledDate={disabledDate}
                getPopupContainer={() => document.body}
                ranges={{
                  'This Week': [dayjs().startOf('week'), dayjs().endOf('week')],
                  'Last Month': [
                    dayjs().subtract(1, 'month').startOf('month'),
                    dayjs().subtract(1, 'month').endOf('month'),
                  ],
                  'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
                  'This Year': [dayjs().startOf('year'), dayjs().endOf('year')],
                }}
              />
              <Button
                icon={<Icons.ReloadOutlined />}
                onClick={fetchReport}
                loading={isLoading}
                style={{ marginLeft: '0.5rem' }}
              />
              <ExportButton
                loading={isExporting}
                onClick={handleExport}
                style={{ marginLeft: '0.5rem' }}
              />
            </div>
          </div>
        </div>
      }
    >
      <style>{`
        .ant-table-cell.ant-table-cell-fix-left,
        .ant-table-cell.ant-table-cell-fix-right {
          background-color: #fff !important;
          z-index: 10 !important;
        }
        .ant-table-thead > tr > th.ant-table-cell.ant-table-cell-fix-left,
        .ant-table-thead > tr > th.ant-table-cell.ant-table-cell-fix-right {
          background-color: #fafafa !important;
          z-index: 11 !important;
        }
        .ant-table-cell-fix-left-last::after {
          box-shadow: inset 10px 0 8px -8px rgba(0,0,0,0.15) !important;
        }
      `}</style>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="lotId"
        loading={isLoading}
        pagination={{
          current,
          pageSize,
          total,
          onChange: (page, size) => {
            setCurrent(page);
            if (size) setPageSize(size);
          },
        }}
        bordered
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
};
